import React, { useEffect, useMemo, useRef, useState } from 'react'

import { EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import { minBy } from 'lodash'
import { toast } from 'react-toastify'
import ReactTooltip from 'react-tooltip'

import { TaskGroupDotsMenu } from '../TaskGroupDotsMenu'
import { AddNewButton } from '../components/AddNewButton'
import GroupSelect from './components/GroupSelect'
import { StageNotes } from './components/StageNotes'
import { TaskForm } from './components/TaskForm'
import { TaskList } from './components/TaskList'
import { useTaskManagement } from './hooks/useTaskManagement'
import { useAppDispatch } from '@/client/hooks'
import FilterBadge from '@/components/FilterBadge'
import { graphqlApi } from '@/graphql'
import { useCurrentApplication } from '@/graphql/services/application'
import {
  useCreateChecklistItemMutation,
  useUpdateCheckListItemSequenceNoMutation,
  useUpdateSubStageMutation,
} from '@/services/application'
import { useUpdateChecklistItemMutation } from '@/services/application'
import {
  useCreateDelayReasonMutation,
  useListDelayReasonsByGroupQuery,
} from '@/services/delayReasons'
import { useGetEscalationsQuery } from '@/services/escalations'
import { useGetLoggedUserQuery } from '@/services/user'
import {
  useCompleteAllItemsFromGroupMutation,
  useGetChecklistGroupsVisibleItemsQuery,
  useUpdateChecklistGroupMutation,
  useUpdateItemsFromTemplateMutation,
} from '@/services/v2-application/checklistGroup'
import { ChecklistGroup, ChecklistItem } from '@/types/unlock'
import {
  ApplicationState,
  applicationStates,
  applicationStatesValues,
} from '@/utils/checklistGroupMappings'

type TaskFilterOptions = 'all' | 'uncompleted' | 'completed'

interface GroupSelectOption {
  value: string
  label: string
  color: string
}

export const TasksV2 = (props) => {
  // Props
  const { activeGroupChange } = props
  // Refs
  const focusRef = useRef(null)
  // Application Data Queries
  const dispatch = useAppDispatch()
  const {
    data: application,
    isLoading: isLoadingApplication,
    isFetching: isFetchingApplication,
  } = useCurrentApplication()
  const { data: user, isLoading: isLoadingUser } = useGetLoggedUserQuery()
  const {
    data: checklist_groups,
    isLoading: isLoadingCLG,
    isFetching: isFetchingCLG,
    refetch: refetchChecklistGroups,
  } = useGetChecklistGroupsVisibleItemsQuery(application?.uuid, {
    skip: !application,
  })
  const { data: escalations } = useGetEscalationsQuery(application?.externalId, {
    skip: !application?.externalId,
  })
  // Mutations
  const [createChecklistItem] = useCreateChecklistItemMutation()
  const [updateChecklistItem] = useUpdateChecklistItemMutation()
  const [updateChecklistGroup] = useUpdateChecklistGroupMutation()
  const [createDelayReason] = useCreateDelayReasonMutation()
  const [completeAllItemsFromGroup] = useCompleteAllItemsFromGroupMutation()
  const [updateItemsFromTemplate] = useUpdateItemsFromTemplateMutation()
  const [updateSubStage, { isLoading: isUpdating }] = useUpdateSubStageMutation()
  const [updateCheckListItemSequenceNo, { isLoading: isUpdatingSequence }] =
    useUpdateCheckListItemSequenceNoMutation()
  // Local State
  const [taskFilterOption, setTaskFilterOption] = useState<TaskFilterOptions>('uncompleted')
  const [creatingTask, setCreatingTask] = useState(false)
  const [editingNotes, setEditingNotes] = useState<boolean>(false)
  const [showingDotsMenu, setShowingDotsMenu] = useState(false)
  const applicationStage: string = useMemo(() => {
    return application?.applicationState
  }, [application?.applicationState])
  const [viewingStage, setViewingStage] = useState<string>(applicationStage)
  // Memoized Values
  const hasEscalationsNotApproved = useMemo(
    () =>
      escalations?.some((escalation) => !escalation.approved) &&
      application?.applicationState === applicationStatesValues.FILE_SETUP,
    [escalations, application]
  )
  // Group Selection and Navigation
  const groupSelectOptions = useMemo<GroupSelectOption[]>(() => {
    if (!application) return []

    const EXCLUDE_CLOSED_STATES = -2
    return applicationStates.slice(0, EXCLUDE_CLOSED_STATES).map((group) => ({
      value: group.application_state,
      label: group.type_name,
      color: group.color,
    }))
  }, [application])

  const activeGroup = useMemo<ApplicationState | undefined>(
    () => applicationStates.find((state) => state.application_state === viewingStage),
    [viewingStage]
  )

  const activeGroupObject = useMemo<ChecklistGroup | undefined>(() => {
    const checklistGroups = checklist_groups?.results || []
    const group = checklistGroups.find((item) => item.type_name === activeGroup?.type_name)

    // Notify parent of group change
    activeGroupChange(group)
    return group
  }, [activeGroup?.type_name, checklist_groups?.results, activeGroupChange])

  const activeGroupItems = useMemo<ChecklistItem[]>(
    () => (activeGroupObject?.checklist_items || []).filter((item) => !item.deleted_at),
    [activeGroupObject?.checklist_items]
  )

  const activeGroupNotes = activeGroupObject?.group_comments

  const { data: delayReasons } = useListDelayReasonsByGroupQuery(activeGroupObject?.id, {
    skip: !activeGroupObject,
  })

  const { tasks, handleDragEnd } = useTaskManagement(activeGroupItems)

  const tasksToShow = useMemo<ChecklistItem[]>(() => {
    const filterMap = {
      uncompleted: () => tasks.filter((task) => task.status !== 'done'),
      completed: () => tasks.filter((task) => task.status === 'done'),
      all: () => tasks,
    }

    return filterMap[taskFilterOption]?.() || tasks
  }, [taskFilterOption, tasks])

  const nextGroup = useMemo<ApplicationState | undefined>(() => {
    const currentIndex = applicationStates.findIndex(
      (state) => state.application_state === applicationStage
    )

    if (currentIndex === -1) return undefined

    const NEXT_INDEX = 1
    return applicationStates[currentIndex + NEXT_INDEX]
  }, [applicationStage])

  // Set focus to "notes" input box
  useEffect(() => {
    focusRef.current && focusRef.current.focus()
  }, [editingNotes])

  useEffect(() => {
    if (applicationStage) setViewingStage(applicationStage)
  }, [applicationStage])

  useEffect(() => {
    ReactTooltip.rebuild()
  })

  const handleBlur = (name: string, notes: string, event: React.FocusEvent) => {
    const newFocus = event.relatedTarget?.id

    // When the focus changes but stays in the form, don't proceed
    if (['name', 'notes'].includes(newFocus)) return

    createTask(name, notes)
  }

  const handleKeyDown = (name: string, notes: string, event: React.KeyboardEvent) => {
    if (event.ctrlKey && event.key === 'Enter') {
      createTask(name, notes)
    }
  }

  const createTask = (name: string, notes: string) => {
    const checklistGroupId = checklist_groups?.results.find(
      (group) => group.type_name === activeGroup?.type_name
    )

    if (!checklistGroupId) return

    if (name) {
      const lowestSequenceNo = minBy(tasks, 'sequence_no')?.sequence_no || 0

      createChecklistItem({
        name,
        notes,
        sequence_no: lowestSequenceNo - 1,
        application: application?.id,
        checklist_group: checklistGroupId.id,
        status: 'not_done',
      })
      setCreatingTask(false)
    }
  }

  const copyDelayReasonsToNextStage = (nextGroupObject: ChecklistGroup) => {
    // When advancing to the next stage, copy the delay reasons from this stage
    delayReasons?.map((reason) => {
      createDelayReason({
        checklist_group: nextGroupObject.id,
        name: reason.name,
      })
    })
  }

  const goToNextStage = () => {
    const nextGroupObject = checklist_groups?.results?.find(
      (item) => item.type_name === nextGroup?.type_name
    )

    copyDelayReasonsToNextStage(nextGroupObject)

    const nextGroupState = nextGroup?.application_state

    // Update checklist group with the latest items from template
    updateItemsFromTemplate({
      applicationId: application.uuid,
      checklistGroupId: nextGroupObject.uuid,
    })

    updateSubStage({
      external_id: application.externalId,
      application_state: nextGroupState,
    }).then((result) => {
      if ('error' in result) {
        const errorDetail =
          (result?.error && 'data' in result.error && (result.error as any).data?.detail) ||
          'Something went wrong.'
        toast.error(errorDetail, {
          containerId: 'global',
          position: 'top-center',
        })
        return
      } else {
        dispatch(graphqlApi.util.invalidateTags(['gql:application']))
      }
    })
  }

  const updateStageNotes = (notes: string) => {
    if (!activeGroupObject) return

    // Only update if there are changes
    if (notes !== activeGroupNotes) {
      updateChecklistGroup({
        applicationUUID: application.uuid,
        checklistGroup: {
          uuid: activeGroupObject.uuid,
          group_comments: notes || null,
        },
      })
    }

    setEditingNotes(false)
  }

  const completeAllItems = async () => {
    const checklistGroup = checklist_groups?.results.find(
      (group) => group.type_name === activeGroup?.type_name
    )

    if (!checklistGroup) return

    await completeAllItemsFromGroup({
      applicationId: application.externalId,
      checklistGroupId: checklistGroup.uuid,
    })
    refetchChecklistGroups()
  }

  const toggleTask = async (task: ChecklistItem) => {
    const newStatus = task.status === 'done' ? null : 'done'
    await updateChecklistItem({
      ...task,
      status: newStatus,
    })
    refetchChecklistGroups()
  }

  if (!application) {
    return <p className='p-4'>Please select an application</p>
  }

  return (
    <div className='p-1'>
      <div className='px-3 flex justify-end'>
        <AddNewButton onClick={() => setCreatingTask(!creatingTask)}>Add New</AddNewButton>
      </div>

      <div className='flex gap-2 items-center justify-between p-3'>
        <div className='grow'>
          <GroupSelect
            options={groupSelectOptions}
            value={viewingStage}
            onChange={(option) => {
              setEditingNotes(false)
              setCreatingTask(false)
              setViewingStage(option.value)
            }}
            isLoading={isUpdating || isLoadingApplication}
            isDisabled={isUpdating || isLoadingApplication}
          />
        </div>
        {/* Only show menu for stages that have checklist group object */}
        {activeGroupObject && (
          <div
            className='relative'
            onMouseEnter={() => setShowingDotsMenu(true)}
            onMouseLeave={() => setShowingDotsMenu(false)}
          >
            <EllipsisVerticalIcon
              width={20}
              className='shrink-0 cursor-pointer'
            />
            {showingDotsMenu && (
              <div className='absolute right-[20px] top-[-10px]'>
                <TaskGroupDotsMenu setEditingNotes={setEditingNotes} />
              </div>
            )}
          </div>
        )}
      </div>

      <StageNotes
        editingNotes={editingNotes}
        activeGroupNotes={activeGroupNotes}
        setEditingNotes={setEditingNotes}
        updateStageNotes={updateStageNotes}
      />

      {creatingTask && (
        <TaskForm
          onSubmit={createTask}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      )}

      <div className='flex gap-2 pb-2 px-2'>
        <FilterBadge
          filterKey={'all'}
          handleSelect={setTaskFilterOption}
          activeTab={taskFilterOption}
          label={'All'}
        />
        <FilterBadge
          filterKey={'completed'}
          handleSelect={setTaskFilterOption}
          activeTab={taskFilterOption}
          label={'Completed Tasks'}
        />
        <FilterBadge
          filterKey={'uncompleted'}
          handleSelect={setTaskFilterOption}
          activeTab={taskFilterOption}
          label={'Uncompleted Tasks'}
        />
      </div>

      {(isLoadingApplication || isFetchingApplication || isLoadingCLG || isFetchingCLG) && (
        <div className='flex justify-center p-3'>
          <div className='loading loading-sm loading-spinner' />
          <div className='text-sm ml-2'>loading tasks...</div>
        </div>
      )}

      <TaskList
        tasks={tasks}
        tasksToShow={tasksToShow}
        taskFilterOption={taskFilterOption}
        isUpdatingSequence={isUpdatingSequence}
        isLoadingApplication={isLoadingApplication}
        completeAllItems={completeAllItems}
        toggleTask={toggleTask}
        handleDragEnd={handleDragEnd}
        viewingStage={viewingStage}
        stageName={
          applicationStates.find((state) => state.application_state === viewingStage)?.type_name ||
          ''
        }
        applicationStage={applicationStage}
        nextGroup={nextGroup}
        goToNextStage={goToNextStage}
        hasEscalationsNotApproved={hasEscalationsNotApproved}
        isUpdating={isUpdating}
        isLoadingUser={isLoadingUser}
        user={user}
        updateCheckListItemSequenceNo={updateCheckListItemSequenceNo}
      />
    </div>
  )
}
